// Reconciliation (SM-714): broker-service's own latest funding run (E1
// per root and currency, findings) and latest revenue run (per node and
// currency: ledger vs earnings), both TRIMMED BY THE SERVER to the
// caller's scope and to the selected currency, whose rows alone decide
// each run's status badge (SM-939: a legacy RED finding never turns the
// GOLD page). Discrepancy CASES are phase 08d (SM-724): while
// cases_available is false the screen says so plainly and invents none.
//
//   GET /api/broker/{id}/reconciliation?currency=   StaffReconciliationResponse
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useScope } from "@/components/layout/ScopeContext";
import { SectionCard } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Money } from "@/components/ui/Money";
import { Id, TableFrame, Time } from "@/components/ui/Common";
import { EmptyState, QueryBlock } from "@/components/ui/States";
import { fetchReconciliation, keys } from "@/lib/queries";
import type { FundingReconRunItem, RevenueReconRunItem, StaffReconciliationResponse } from "@/lib/types";
import type { CurrencyCode } from "@/lib/currency";
import { useI18n } from "@/i18n/I18nProvider";
import { labelOf, reconFiguresAreMoney, reconFindingDetail, serverCaption } from "@/i18n/labels";

export const Route = createFileRoute("/_app/reconciliation")({
  beforeLoad: capabilityGuard("FINANCE_VIEW"),
  component: ReconciliationPage,
});

function ReconciliationPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const { nodeId, currency } = useScope();
  const probe = { queryKey: keys.reconciliation(nodeId, currency), queryFn: () => fetchReconciliation(nodeId, currency) };
  const q = useQuery({ ...probe, enabled: guard.kind === "allow" });
  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader title={t("recon.title")} subtitle={t("recon.subtitle")} coverage="subtree" money />
      <QueryBlock query={q} lines={6}>
        {(r) => <Recon r={r} currency={currency} />}
      </QueryBlock>
    </Guarded>
  );
}

function Recon({ r, currency }: { r: StaffReconciliationResponse; currency: CurrencyCode }) {
  const { t } = useI18n();
  return (
    <div className="space-y-4">
      <SectionCard title={t("recon.funding")}>
        {r.funding_run ? <FundingRun run={r.funding_run} currency={currency} /> : <EmptyState title={t("recon.noFundingRun")} />}
      </SectionCard>
      <SectionCard title={t("recon.revenue")}>
        {r.revenue_run ? <RevenueRun run={r.revenue_run} currency={currency} /> : <EmptyState title={t("recon.noRevenueRun")} />}
      </SectionCard>
      <SectionCard title={t("recon.cases")}>
        {r.cases_available ? (
          <p className="text-sm text-t-2">{t("recon.casesAvailable")}</p>
        ) : (
          <EmptyState title={t("recon.casesLater")} hint={t("recon.casesLaterHint")} />
        )}
      </SectionCard>
      <p className="text-2xs text-t-3">
        {t("common.asOf")} <Time ms={r.as_of_unix_ms} />
      </p>
    </div>
  );
}

function RunHeader({ status, tone, started, finished, id, error }: { status: string; tone: "yes" | "warn" | "no" | "accent"; started: number; finished?: number; id: string; error?: string }) {
  const { t } = useI18n();
  return (
    <div className="mb-3 space-y-1">
      <div className="flex flex-wrap items-center gap-2 text-xs text-t-2">
        <Badge tone={tone}>{status}</Badge>
        <span>
          {t("recon.started")} <Time ms={started} />
        </span>
        {finished ? (
          <span>
            {t("recon.finished")} <Time ms={finished} />
          </span>
        ) : null}
        <Id value={id} />
      </div>
      {error && <p className="text-xs text-no">{t("recon.runError", { error: serverCaption(t, error) })}</p>}
    </div>
  );
}

function FundingRun({ run, currency }: { run: FundingReconRunItem; currency: CurrencyCode }) {
  const { t } = useI18n();
  const roots = (run.roots ?? []).filter((x) => x.currency === currency);
  const findings = (run.findings ?? []).filter((f) => !f.currency || f.currency === currency);
  const tone = run.status === "OK" ? "yes" : run.status === "FAILED" ? "no" : "warn";
  return (
    <>
      <RunHeader
        status={labelOf(t, "fundingRecon", run.status)}
        tone={tone}
        started={run.started_at_unix_ms}
        finished={run.finished_at_unix_ms}
        id={run.run_id}
        error={run.error}
      />
      {roots.length === 0 ? (
        <p className="text-sm text-t-3">{t("recon.noRootsInCurrency", { currency })}</p>
      ) : (
        <TableFrame label={t("recon.funding")}>
          <table className="tbl">
            <thead>
              <tr>
                <th scope="col">{t("recon.root")}</th>
                <th scope="col" className="text-right">
                  {t("recon.topUps")}
                </th>
                <th scope="col" className="text-right">
                  {t("recon.payoutsCompleted")}
                </th>
                <th scope="col" className="text-right">
                  {t("recon.nodeTotal")}
                </th>
                <th scope="col" className="text-right">
                  {t("recon.userAllocations")}
                </th>
                <th scope="col" className="text-right">
                  {t("recon.userReturns")}
                </th>
                <th scope="col">{t("recon.balanced")}</th>
              </tr>
            </thead>
            <tbody>
              {roots.map((x) => (
                <tr key={`${x.root_broker_id}-${x.currency}`}>
                  <td>
                    <Id value={x.root_broker_id} />
                  </td>
                  <td className="text-right">
                    <Money currency={x.currency} value={x.top_ups} />
                  </td>
                  <td className="text-right">
                    <Money currency={x.currency} value={x.payouts_completed} />
                  </td>
                  <td className="text-right">
                    <Money currency={x.currency} value={x.node_total} />
                  </td>
                  <td className="text-right">
                    <Money currency={x.currency} value={x.user_allocations} />
                  </td>
                  <td className="text-right">
                    <Money currency={x.currency} value={x.user_returns} />
                  </td>
                  <td>
                    <Badge tone={x.balanced ? "yes" : "no"}>{x.balanced ? t("recon.yes") : t("recon.no")}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableFrame>
      )}
      <h3 className="section-title mb-2 mt-4">{t("recon.findings")}</h3>
      {findings.length === 0 ? (
        <p className="text-sm text-t-3">{t("recon.noFindings")}</p>
      ) : (
        <TableFrame label={t("recon.findings")}>
          <table className="tbl">
            <thead>
              <tr>
                <th scope="col">{t("recon.rule")}</th>
                <th scope="col">{t("recon.severity")}</th>
                <th scope="col">{t("recon.node")}</th>
                <th scope="col">{t("recon.detail")}</th>
                <th scope="col" className="text-right">
                  {t("recon.expected")}
                </th>
                <th scope="col" className="text-right">
                  {t("recon.actual")}
                </th>
              </tr>
            </thead>
            <tbody>
              {findings.map((f, i) => (
                <tr key={`${f.rule}-${f.broker_id ?? ""}-${f.operation_id ?? ""}-${i}`}>
                  <td className="mono text-xs">{f.rule}</td>
                  <td>
                    <Badge tone={f.severity === "CRITICAL" || f.severity === "ERROR" ? "no" : "warn"}>{labelOf(t, "reconSeverity", f.severity)}</Badge>
                  </td>
                  <td>
                    <Id value={f.broker_id || f.root_broker_id} />
                  </td>
                  <td className="max-w-80 text-xs">{reconFindingDetail(t, f)}</td>
                  <td className="text-right">
                    <FindingFigure value={f.expected} money={reconFiguresAreMoney(f)} currency={f.currency || currency} />
                  </td>
                  <td className="text-right">
                    <FindingFigure value={f.actual} money={reconFiguresAreMoney(f)} currency={f.currency || currency} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableFrame>
      )}
    </>
  );
}

/**
 * A finding's expected or actual figure: money when the finding compares
 * amounts, else the value as sent — a currency, a count, a wallet id or
 * the rail's answer is never formatted as money (SM-957).
 */
function FindingFigure({ value, money, currency }: { value: string | undefined; money: boolean; currency: string }) {
  if (!value) return <>—</>;
  if (money) return <Money currency={currency} value={value} />;
  return <span className="mono break-all text-xs">{value}</span>;
}

function RevenueRun({ run, currency }: { run: RevenueReconRunItem; currency: CurrencyCode }) {
  const { t } = useI18n();
  const items = (run.items ?? []).filter((x) => x.currency === currency);
  const tone = run.state === "MATCHED" ? "yes" : run.state === "RUNNING" ? "accent" : run.state === "FAILED" ? "no" : "warn";
  return (
    <>
      <RunHeader
        status={labelOf(t, "reconState", run.state)}
        tone={tone}
        started={run.started_at_unix_ms}
        finished={run.finished_at_unix_ms}
        id={run.run_id}
        error={run.error}
      />
      {items.length === 0 ? (
        <p className="text-sm text-t-3">{t("recon.noItemsInCurrency", { currency })}</p>
      ) : (
        <TableFrame label={t("recon.revenue")}>
          <table className="tbl">
            <thead>
              <tr>
                <th scope="col">{t("recon.node")}</th>
                <th scope="col" className="text-right">
                  {t("recon.ledgerPending")}
                </th>
                <th scope="col" className="text-right">
                  {t("recon.earningsPending")}
                </th>
                <th scope="col" className="text-right">
                  {t("recon.ledgerAvailable")}
                </th>
                <th scope="col" className="text-right">
                  {t("recon.earningsAvailable")}
                </th>
                <th scope="col">{t("recon.matched")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((x) => (
                <tr key={`${x.broker_id}-${x.currency}`}>
                  <td>
                    <Id value={x.broker_id} />
                  </td>
                  <td className="text-right">
                    <Money currency={x.currency} value={x.ledger_pending} />
                  </td>
                  <td className="text-right">
                    <Money currency={x.currency} value={x.earnings_pending} />
                  </td>
                  <td className="text-right">
                    <Money currency={x.currency} value={x.ledger_available} />
                  </td>
                  <td className="text-right">
                    <Money currency={x.currency} value={x.earnings_available} />
                  </td>
                  <td>
                    {x.in_flight ? (
                      <Badge tone="accent">{t("recon.inFlight")}</Badge>
                    ) : (
                      <Badge tone={x.matched ? "yes" : "no"}>{x.matched ? t("recon.yes") : t("recon.no")}</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableFrame>
      )}
    </>
  );
}
